import { useCallback, useEffect, useState } from 'react';
import { logger } from '@/lib/logger';
import {
  usePowerSync,
  useWatchQueryCache,
} from '../contexts/power-sync/PowerSync';
import type { QueryBuilderSpec } from '../types';

interface QueryStateArray<T> {
  data: T[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface QueryStateSingle<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface UseLivePowerSyncQueryOptionsArray<T> {
  enabled?: boolean;
  single?: false;
  onSuccess?: (data: T[]) => void;
  onError?: (error: Error) => void;
}

interface UseLivePowerSyncQueryOptionsSingle<T> {
  enabled?: boolean;
  single: true;
  onSuccess?: (data: T | null) => void;
  onError?: (error: Error) => void;
}

/**
 * Custom hook for reactive PowerSync queries with Kysely
 * Provides real-time updates when data changes in the database
 *
 * @param queryBuilder - Query builder specification with createQuery function and queryKey
 * @param deps - Dependencies array for the query (similar to useEffect deps)
 * @param options - Optional configuration
 */
export function useLivePowerSyncQuery<T>(
  queryBuilder: QueryBuilderSpec<T> | null,
  deps: React.DependencyList,
  options: UseLivePowerSyncQueryOptionsSingle<T>,
): QueryStateSingle<T>;
export function useLivePowerSyncQuery<T>(
  queryBuilder: QueryBuilderSpec<T> | null,
  deps?: React.DependencyList,
  options?: UseLivePowerSyncQueryOptionsArray<T>,
): QueryStateArray<T>;
export function useLivePowerSyncQuery<T>(
  queryBuilder: QueryBuilderSpec<T> | null,
  deps: React.DependencyList = [],
  options:
    | UseLivePowerSyncQueryOptionsArray<T>
    | UseLivePowerSyncQueryOptionsSingle<T> = {},
): QueryStateArray<T> | QueryStateSingle<T> {
  const powerSync = usePowerSync();
  const { subscribeToQuery } = useWatchQueryCache();
  const [data, setData] = useState<T[] | T | null>(options.single ? null : []);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const {
    enabled = true,
    single = false,
    onSuccess,
    onError: onErrorCallback,
  } = options;

  const executeQuery = useCallback(async () => {
    if (!queryBuilder || !queryBuilder.createQuery) {
      logger.warn('useLivePowerSyncQuery: queryBuilder is null');
      setData(single ? null : []);
      setIsLoading(false);
      return;
    }
    if (!powerSync || !enabled) return;

    try {
      setIsLoading(true);
      setError(null);

      const query = queryBuilder.createQuery(powerSync);
      const result = await query.execute();

      if (single) {
        const singleResult = result.length > 0 ? result[0] : null;
        setData(singleResult);
        onSuccess?.(singleResult);
      } else {
        setData(result);
        onSuccess?.(result);
      }

      setIsLoading(false);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Query failed');
      setError(error);
      setIsLoading(false);
      onErrorCallback?.(error);
    }
  }, [
    powerSync,
    enabled,
    single,
    onSuccess,
    onErrorCallback,
    queryBuilder,
    ...deps,
  ]);

  const refetch = useCallback(() => {
    executeQuery();
  }, [executeQuery]);

  // Compute queryKeyString outside useEffect so it can be used as a dependency
  const queryKeyString = queryBuilder
    ? JSON.stringify(
        Array.isArray(queryBuilder.queryKey)
          ? queryBuilder.queryKey
          : [queryBuilder.queryKey],
      )
    : null;

  useEffect(() => {
    if (!powerSync || !enabled) {
      setIsLoading(false);
      return;
    }

    if (!queryBuilder || !queryBuilder.createQuery || !queryKeyString) {
      setData(single ? null : []);
      setIsLoading(false);
      return;
    }

    // Subscribe to shared watch query
    const unsubscribe = subscribeToQuery<T>(
      queryKeyString,
      queryBuilder,
      (results) => {
        if (single) {
          const singleResult = results.length > 0 ? results[0] : null;
          setData(singleResult);
          onSuccess?.(singleResult as T & T[]);
        } else {
          setData(results);
          onSuccess?.(results as T & T[]);
        }

        setIsLoading(false);
        setError(null);
      },
    );

    return unsubscribe;
  }, [powerSync, enabled, single, subscribeToQuery, queryKeyString, onSuccess]);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}
